import { type FC } from "react";

import { cx } from "@emotion/css";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { type LensMetadata } from "../../gql-generated/insights-types";
import { CofConfigId } from "../../gql-generated/types";
import { AgeChartComponent } from "../../lens-insights/components/audience-tab";
import { ChartCard } from "../../lens-insights/components/shared";
import { Flexbox } from "../../shared/components";
import { useCommunityAudiencePageBlizzardEvent } from "../hooks";
import { useFilter } from "../hooks/useFilter";

import { CommunityAudienceDemographics } from "./CommunityAudienceDemographics";
import { CommunityAudienceHeader } from "./CommunityAudienceHeader";
import { HorizontalBarChartCard } from "./HorizontalBarChartCard";
import { OSBreakdown } from "./OSBreakdown";
import { TopLensesContainer } from "./TopLensesContainer";
import {
    CommunityAudiencePageContainer,
    ageBreakdown,
    flexGrow,
    halfScreenCardLeft,
    halfScreenCardRight,
} from "./shared";

export const CommunityAudiencePage: FC = () => {
    useCommunityAudiencePageBlizzardEvent();
    const { configs } = useFeatureGates();
    const shouldShowMetrics = !configs[CofConfigId.MyLensesUiCommunityAudienceMetricsHidden].enabled;
    const { isLoading, filterTree, data, parsedInsightsData, lensMetadata, lensMetadataLoading, onFilter, onReset } =
        useFilter();

    return (
        <CommunityAudiencePageContainer>
            <CommunityAudienceHeader filterTree={filterTree} onFilterCheck={onFilter} onResetFilters={onReset} />
            <CommunityAudienceDemographics
                demographicsData={data && parsedInsightsData ? parsedInsightsData.demographicsData : null}
                loading={isLoading}
            />
            <Flexbox>
                <ChartCard className={cx(ageBreakdown, flexGrow)} title="Demographics">
                    <AgeChartComponent
                        metrics={data && parsedInsightsData ? parsedInsightsData.ageGenderMetrics : { isEmpty: true }}
                        loading={isLoading}
                    />
                </ChartCard>
                <OSBreakdown stats={data && parsedInsightsData ? parsedInsightsData.osData : []} />
            </Flexbox>
            <Flexbox>
                <HorizontalBarChartCard
                    title="Top Countries"
                    className={halfScreenCardLeft}
                    stats={data && parsedInsightsData ? parsedInsightsData.countryStats : []}
                    loading={isLoading}
                />
                <HorizontalBarChartCard
                    title="Top Languages"
                    className={halfScreenCardRight}
                    stats={data && parsedInsightsData ? parsedInsightsData.languageStats : []}
                    loading={isLoading}
                />
            </Flexbox>
            <>
                <Flexbox>
                    <HorizontalBarChartCard
                        title="Top Device Manufacturers & Models"
                        className={halfScreenCardLeft}
                        stats={data && parsedInsightsData ? parsedInsightsData.deviceStats : []}
                        loading={isLoading}
                    />
                </Flexbox>
                {shouldShowMetrics && (
                    <Flexbox>
                        <TopLensesContainer
                            loading={lensMetadataLoading}
                            lensMetadata={lensMetadata?.getLensMetadata as LensMetadata[]}
                        />
                    </Flexbox>
                )}
            </>
        </CommunityAudiencePageContainer>
    );
};
